/**
 * Sample scenes.
 *
 * These are drawn procedurally on a canvas rather than shipped as photographs —
 * no network fetch, no licensing question, and no binary in the repo. They are
 * synthetic, and the UI labels them as such.
 *
 * They are not decoration: each one is built to exercise a different branch of
 * the analyzer — subject on the right with open space left, a bright high-key
 * frame that must flip to dark type, a dark low-key frame, and a busy textured
 * frame that should force a panel treatment. The skin-toned subject also gives
 * the face heuristic something real to find.
 */

export interface SampleScene {
  id: string;
  label: string;
  note: string;
  build: (ctx: CanvasRenderingContext2D, w: number, h: number) => void;
}

function grain(ctx: CanvasRenderingContext2D, w: number, h: number, amount: number): void {
  const image = ctx.getImageData(0, 0, w, h);
  const { data } = image;
  for (let i = 0; i < data.length; i += 4) {
    const n = (Math.random() - 0.5) * amount;
    data[i] += n;
    data[i + 1] += n;
    data[i + 2] += n;
  }
  ctx.putImageData(image, 0, 0);
}

function vignette(ctx: CanvasRenderingContext2D, w: number, h: number, strength: number): void {
  const grad = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.2, w / 2, h / 2, Math.max(w, h) * 0.75);
  grad.addColorStop(0, 'rgba(0,0,0,0)');
  grad.addColorStop(1, `rgba(0,0,0,${strength})`);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
}

/** A simplified head-and-shoulders figure in skin tones. */
function figure(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  scale: number,
  skin: string,
  shade: string,
  hair: string,
): void {
  // Shoulders
  ctx.fillStyle = shade;
  ctx.beginPath();
  ctx.ellipse(cx, cy + scale * 1.5, scale * 1.5, scale * 1.1, 0, 0, Math.PI * 2);
  ctx.fill();

  // Neck
  ctx.fillStyle = skin;
  ctx.fillRect(cx - scale * 0.24, cy + scale * 0.5, scale * 0.48, scale * 0.6);

  // Hair mass behind the head
  ctx.fillStyle = hair;
  ctx.beginPath();
  ctx.ellipse(cx, cy - scale * 0.12, scale * 0.76, scale * 0.92, 0, 0, Math.PI * 2);
  ctx.fill();

  // Face
  const faceGrad = ctx.createLinearGradient(cx - scale, cy - scale, cx + scale, cy + scale);
  faceGrad.addColorStop(0, skin);
  faceGrad.addColorStop(1, shade);
  ctx.fillStyle = faceGrad;
  ctx.beginPath();
  ctx.ellipse(cx, cy, scale * 0.62, scale * 0.82, 0, 0, Math.PI * 2);
  ctx.fill();

  // Brow and eye shadows — enough structure that the region is not a flat blob
  ctx.fillStyle = 'rgba(60,40,32,0.34)';
  ctx.beginPath();
  ctx.ellipse(cx - scale * 0.25, cy - scale * 0.16, scale * 0.14, scale * 0.06, 0, 0, Math.PI * 2);
  ctx.ellipse(cx + scale * 0.25, cy - scale * 0.16, scale * 0.14, scale * 0.06, 0, 0, Math.PI * 2);
  ctx.fill();
}

export const SAMPLE_SCENES: SampleScene[] = [
  {
    id: 'portrait-right',
    label: 'Portrait, open left',
    note: 'Subject right of center — the engine should move type into the left negative space.',
    build: (ctx, w, h) => {
      const grad = ctx.createLinearGradient(0, 0, w, h);
      grad.addColorStop(0, '#2b3038');
      grad.addColorStop(0.55, '#3d4450');
      grad.addColorStop(1, '#1d2026');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      // Soft falloff behind the subject
      const spot = ctx.createRadialGradient(w * 0.68, h * 0.42, 0, w * 0.68, h * 0.42, w * 0.5);
      spot.addColorStop(0, 'rgba(255,238,214,0.28)');
      spot.addColorStop(1, 'rgba(255,238,214,0)');
      ctx.fillStyle = spot;
      ctx.fillRect(0, 0, w, h);

      figure(ctx, w * 0.68, h * 0.42, h * 0.14, '#e0b193', '#b3846a', '#241a16');
      vignette(ctx, w, h, 0.34);
      grain(ctx, w, h, 9);
    },
  },
  {
    id: 'high-key',
    label: 'High key, bright',
    note: 'Very light frame — the engine should flip to dark type rather than white-on-white.',
    build: (ctx, w, h) => {
      const grad = ctx.createLinearGradient(0, 0, 0, h);
      grad.addColorStop(0, '#fdfbf7');
      grad.addColorStop(0.6, '#f1ece3');
      grad.addColorStop(1, '#e4ddd1');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      ctx.fillStyle = 'rgba(214,203,186,0.5)';
      ctx.beginPath();
      ctx.ellipse(w * 0.3, h * 0.78, w * 0.5, h * 0.24, 0, 0, Math.PI * 2);
      ctx.fill();

      figure(ctx, w * 0.34, h * 0.46, h * 0.13, '#f0cbb0', '#d3a288', '#3b2a22');

      const warm = ctx.createRadialGradient(w * 0.8, h * 0.2, 0, w * 0.8, h * 0.2, w * 0.6);
      warm.addColorStop(0, 'rgba(255,250,240,0.85)');
      warm.addColorStop(1, 'rgba(255,250,240,0)');
      ctx.fillStyle = warm;
      ctx.fillRect(0, 0, w, h);
      grain(ctx, w, h, 6);
    },
  },
  {
    id: 'low-key',
    label: 'Low key, dark',
    note: 'Deep shadow frame — light type with a gentle scrim should be enough.',
    build: (ctx, w, h) => {
      ctx.fillStyle = '#0b0d11';
      ctx.fillRect(0, 0, w, h);

      const rim = ctx.createLinearGradient(w * 0.2, 0, w, h);
      rim.addColorStop(0, 'rgba(74,92,120,0.55)');
      rim.addColorStop(0.5, 'rgba(30,38,52,0.35)');
      rim.addColorStop(1, 'rgba(8,10,14,0)');
      ctx.fillStyle = rim;
      ctx.fillRect(0, 0, w, h);

      figure(ctx, w * 0.52, h * 0.5, h * 0.15, '#8d6a55', '#4a352a', '#0c0908');

      const edge = ctx.createLinearGradient(w * 0.3, h * 0.2, w * 0.75, h * 0.8);
      edge.addColorStop(0, 'rgba(255,214,170,0.16)');
      edge.addColorStop(1, 'rgba(255,214,170,0)');
      ctx.fillStyle = edge;
      ctx.fillRect(0, 0, w, h);

      vignette(ctx, w, h, 0.5);
      grain(ctx, w, h, 11);
    },
  },
  {
    id: 'busy-texture',
    label: 'Busy texture',
    note: 'High detail everywhere — the engine should reach for a panel or blur to stay readable.',
    build: (ctx, w, h) => {
      ctx.fillStyle = '#4a4036';
      ctx.fillRect(0, 0, w, h);

      // Overlapping foliage-like shapes produce genuinely high local variance,
      // which is what the busyness detector keys off.
      for (let i = 0; i < 260; i++) {
        const x = Math.random() * w;
        const y = Math.random() * h;
        const r = 18 + Math.random() * 120;
        const hue = 60 + Math.random() * 70;
        const light = 18 + Math.random() * 46;
        ctx.fillStyle = `hsla(${hue}, ${28 + Math.random() * 34}%, ${light}%, 0.5)`;
        ctx.beginPath();
        ctx.ellipse(x, y, r, r * (0.35 + Math.random() * 0.5), Math.random() * Math.PI, 0, Math.PI * 2);
        ctx.fill();
      }

      const light = ctx.createRadialGradient(w * 0.3, h * 0.25, 0, w * 0.3, h * 0.25, w * 0.7);
      light.addColorStop(0, 'rgba(255,240,200,0.34)');
      light.addColorStop(1, 'rgba(255,240,200,0)');
      ctx.fillStyle = light;
      ctx.fillRect(0, 0, w, h);

      vignette(ctx, w, h, 0.3);
      grain(ctx, w, h, 14);
    },
  },
];

const cache = new Map<string, string>();

export function renderSampleScene(id: string, width = 1400, height = 1750): string {
  const cached = cache.get(id);
  if (cached) return cached;

  const scene = SAMPLE_SCENES.find((s) => s.id === id) ?? SAMPLE_SCENES[0];
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('Canvas 2D is unavailable in this browser.');
  scene.build(ctx, width, height);
  const url = canvas.toDataURL('image/jpeg', 0.92);
  cache.set(id, url);
  return url;
}
